import type { IHowToUseTip } from '@skystone/shared';

// ---------------------------------------------------------------------------
// Inline bold renderer
//
// Tip text uses `**phrase**` for the emphasised lead — e.g.
//   "**Track trend over time**, not just the number."
// We split the string into alternating normal / bold segments and render the
// bold ones inside <strong>. Intentionally no markdown dependency.
//
// Defined at module scope (not inside the component) to satisfy SonarCloud
// rule S7721 and keep renders cheap.
// ---------------------------------------------------------------------------

interface Segment {
  readonly text: string;
  readonly bold: boolean;
}

const BOLD_SEGMENT_PATTERN = /\*\*([^*]+)\*\*/g;

export function splitBoldSegments(text: string): Segment[] {
  const segments: Segment[] = [];
  let cursor = 0;
  for (const match of text.matchAll(BOLD_SEGMENT_PATTERN)) {
    const start = match.index ?? 0;
    if (start > cursor) {
      segments.push({ text: text.slice(cursor, start), bold: false });
    }
    segments.push({ text: match[1], bold: true });
    cursor = start + match[0].length;
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), bold: false });
  }
  return segments;
}

function TipText({ text }: Readonly<{ text: string }>) {
  const segments = splitBoldSegments(text);
  return (
    <span className="text-sm leading-relaxed text-muted-foreground">
      {segments.map((seg, idx) => {
        const key = `${idx}-${seg.text}`;
        return seg.bold ? (
          <strong key={key} className="font-semibold text-foreground">
            {seg.text}
          </strong>
        ) : (
          <span key={key}>{seg.text}</span>
        );
      })}
    </span>
  );
}

// ---------------------------------------------------------------------------
// HowToUseTips — numbered circle bullets
// ---------------------------------------------------------------------------

interface HowToUseTipsProps {
  tips: IHowToUseTip[];
}

export function HowToUseTips({ tips }: Readonly<HowToUseTipsProps>) {
  return (
    <ol className="flex flex-col gap-2" aria-label="How to use this metric">
      {tips.map((tip) => (
        <li key={tip.order} className="flex items-start gap-2">
          <span
            aria-hidden="true"
            className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground"
          >
            {tip.order}
          </span>
          <TipText text={tip.text} />
        </li>
      ))}
    </ol>
  );
}
